import axios from "axios";
import { API_URL } from "./constants";
import { auth } from "./firebase";
import { signOut } from "firebase/auth";

// Status code constants for readability
const UNAUTHORIZED = 401;

/**
 * Axios Instance
 *
 * This axios instance contains the Bearer token and the BASE URL
 * for the API.
 *
 * @returns AxiosInstance
 */
export const axiosInstance = axios.create({
  baseURL: API_URL,
});

type SessionExpiredHandler = () => void | Promise<void>;

// AuthProvider registers its logout here so a dead session tears down React
// state too. The fallback covers a 401 that lands while nothing is mounted.
let sessionExpiredHandler: SessionExpiredHandler | null = null;
export const setSessionExpiredHandler = (handler: SessionExpiredHandler | null) => {
  sessionExpiredHandler = handler;
};

const signOutFallback = async () => {
  localStorage.removeItem("hub");
  try {
    await signOut(auth);
  } finally {
    window.location.assign("/login");
  }
};

// Shared across concurrent 401s: one forced refresh and one sign-out per
// wave, however many requests failed together.
let refreshPromise: Promise<string> | null = null;
const refreshIdToken = () => {
  if (!refreshPromise) {
    const firebaseUser = auth.currentUser;
    refreshPromise = (
      firebaseUser
        ? firebaseUser.getIdToken(true)
        : Promise.reject(new Error("No Firebase user"))
    ).finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
};

let sessionExpiredPromise: Promise<void> | null = null;
const handleSessionExpired = () => {
  if (!sessionExpiredPromise) {
    console.error("Unauthorized: Firebase session expired, signing out");
    sessionExpiredPromise = Promise.resolve()
      .then(() => (sessionExpiredHandler || signOutFallback)())
      .catch((error) => console.error("Failed to sign out expired session", error))
      .finally(() => {
        sessionExpiredPromise = null;
      });
  }
  return sessionExpiredPromise;
};

/**
 * Request interceptor
 * Adds Firebase Bearer auth to the request header if not already present
 */
axiosInstance.interceptors.request.use(
  async (config) => {
    const isAccountData = !!config.url?.startsWith("/account-data/");
    // The scoped grant must keep working after deletion removes the login.
    if (!config.headers["Authorization"] && !(isAccountData && config.headers["X-Account-Data-Access"])) {
      let token: string | undefined;
      try {
        token = await auth.currentUser?.getIdToken();
      } catch (error) {
        // Public account-data routes don't need a login; a stale, disabled or
        // deleted session must not stop them from being sent.
        if (!isAccountData) throw error;
      }
      if (token) {
        config.headers["Authorization"] = `Bearer ${token}`;
      }
    }
    // Firebase stays signed in as the admin while the hub displays another user.
    // Only organization endpoints accept this explicitly authorized scope, and
    // only to read: a write performed "as" someone else would mint their API
    // keys or open a Stripe checkout against their account. The server enforces
    // this too -- this keeps the header off the wire in the first place.
    const method = (config.method || "get").toLowerCase();
    const isRead = method === "get" || method === "head";
    if (isRead && /^\/organization\/me(?:\/|$|\?)/.test(config.url || "")) {
      try {
        const hub = JSON.parse(localStorage.getItem("hub") || "null");
        if (hub?.isImpersonatingFromAdmin && typeof hub.uuid === "string" && hub.uuid) {
          config.headers["X-Impersonate-User-Id"] = hub.uuid;
        }
      } catch {
        // Ignore an invalid persisted session; normal bearer auth still applies.
      }
    }
    return config;
  },
  (error) => Promise.reject(error),
);

/**
 * Response interceptor
 * A 401 on a Firebase-authenticated request is retried once with a forced
 * token refresh. If the refresh fails (or there's no Firebase user), the
 * session is dead (revoked, disabled, password changed elsewhere) and the
 * user is signed out. A retry that still 401s is rejected as usual.
 */
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    // No response at all on a network failure, CORS block or abort.
    const config = error?.config;
    const authorization = config?.headers?.["Authorization"];
    if (
      error?.response?.status !== UNAUTHORIZED ||
      // Requests without a Firebase token (E2E personas, signed-out public
      // pages) have no session to refresh.
      typeof authorization !== "string" ||
      !authorization.startsWith("Bearer ") ||
      // Public share 401s are a wrong password or an expired share token.
      /^\/public\//.test(config.url || "")
    ) {
      return Promise.reject(error);
    }

    // A freshly refreshed token that still 401s means the endpoint uses 401
    // for something other than auth; the session itself is fine.
    if (config._hubAuthRetried) {
      return Promise.reject(error);
    }

    let token: string;
    try {
      token = await refreshIdToken();
    } catch {
      await handleSessionExpired();
      return Promise.reject(error);
    }
    config._hubAuthRetried = true;
    config.headers["Authorization"] = `Bearer ${token}`;
    return axiosInstance(config);
  },
);
